import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $messageCentreStore } from '@lib/store';
import type { ThreadWithMessages } from '@shared/api/rest/messages';
import { MessageWrapper } from './messageWrapper';
import { Button, ErrorMessage, FileInput, Textarea } from '@shared/widgets';

interface IForm {
  message: string;
  file?: File;
}
const DEFAULT_VALUES: IForm = { message: '', file: undefined };

interface IReplyProps {
  thread: ThreadWithMessages;
  onReply: () => void;
}

export const Reply = (props: IReplyProps) => {
  const { t } = useTranslation('messageCenter');

  const { control, formState, handleSubmit, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });

  const onSubmit = (data: IForm) => {
    const formData = new FormData();
    data.file && formData.append('file', data.file);

    $messageCentreStore.events.addNewMessageToThread({
      groupId: props.thread.groupId,
      body: data.message,
      subject: props.thread.subject,
      file: data.file ? formData : undefined,
    });

    reset(DEFAULT_VALUES);
    props.onReply();
  };

  return (
    <MessageWrapper className='pl-8'>
      <form className='flex-auto' noValidate onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-12'>
          <div className='flex flex-col gap-1'>
            <Controller
              name='message'
              control={control}
              rules={{ required: t('errors.form.required', { ns: 'common' }) }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <>
                  <Textarea
                    textareaClassName='m-0'
                    fitContent
                    placeholder={t('thread.reply.placeholders.enterMessage')}
                    required
                    rows={5}
                    value={value}
                    onChange={onChange}
                  />
                  <ErrorMessage text={error?.message} />
                </>
              )}
            />
          </div>
        </div>
        <div className='flex justify-between'>
          <Controller
            name='file'
            control={control}
            render={({ field: { value, onChange } }) => (
              <FileInput
                className='w-72 max-w-[calc(50%-15px)] flex-none'
                value={value}
                onChange={onChange}
                onRemove={() => setValue('file', undefined)}
              />
            )}
          />
          <div className='w-72 max-w-[calc(50%-15px)] flex-none'>
            <Button color='primary' fullWidth type='submit' disabled={formState.isSubmitting}>
              {t('thread.reply.buttons.reply')}
            </Button>
          </div>
        </div>
      </form>
    </MessageWrapper>
  );
};
